import { tournamentAPI } from '../../../../tournaments/services/tournamentApi';
import { tournamentScheduleAPI } from '../../../../tournaments/services/tournamentApi';
import { teamAPI } from '../../../../teams/services/teamApi';
import { buildRoundsWithRows } from '../../../components/lists/scheduleRows';
import { ordinal, buildSeedOrder } from '../../../generators/bracketUtils';
import { applyTieFormatToRounds } from '../../../generators/tieFormats';

/**
 * Save/Load adapter between this module's in-memory schedule shapes
 * (generators/*) and the real backend (tournament_rounds + tournaments.
 * schedule_config — see backend's scheduleGenerator.js generateCustomFormat
 * and the exsports_fastify_apis save-schedule-autoseed-plan.md for the full
 * design). Every other file in this module works in mock-or-real-agnostic
 * terms (see realParticipantsService.js's own doc comment) — this file is
 * the one exception, since Save/Load is inherently a real-tournament-only
 * concept (there is nothing to save a standalone Explore Formats session
 * to). Only useScheduleBuilder.js calls this, and only when `standalone`
 * is false.
 *
 * Deliberately does NOT persist matchOrderOverrides/roundOverrides/
 * rowOverrides as raw override maps — instead, `serializeScheduleForSave`
 * BAKES their resolved effect (court, custom settings, team-pairing, row
 * order) directly into the real tournament_rounds rows it creates (match_number
 * for order, `court` column, `overridden_match_settings`/`participant_a_players`/
 * `participant_b_players`). On load, those overrides start empty (`{}`) —
 * there's nothing left to reapply, the saved rows already reflect them. This
 * sidesteps having to re-key GUID-based override maps onto the real backend
 * ids `generateCustomFormat` assigns at save time (see the plan doc for why
 * that would otherwise be needed).
 *
 * Scope note: a match with an expanded MLP tie slate (subMatches — see
 * generators/tieFormats.js) is currently saved as ONE plain structural row
 * (its parent slotA/slotB), not one row per discipline. The backend's
 * per-row single-winner advancement model doesn't fit "5 sub-games decide
 * one tie's advancing winner" without real design work of its own — MLP
 * slate configuration stays display/planning-only (as it already implicitly
 * was pre-this-feature) until that's tackled separately.
 */

// formatId -> { serialize(schedule, ctx) -> { rounds: [...] } (backend
// scheduleData shape), hydrate(flatRounds, ctx) -> schedule (this module's
// in-memory shape) }. Only single_elimination and round_robin are wired up
// so far (see serializeFlatRounds/hydrateFlatRounds below) — every other
// format falls back to unsupported (Save Schedule stays hidden for it,
// isSaveSupported returns false) until its own adapter is written, likely
// building on top of these two for whichever nested section is itself a
// plain rounds/matches list (e.g. group_knockout's group stage IS a
// round_robin section; its knockout stage IS a single_elimination-shaped
// bracket — see bracketUtils.buildBracket).
const ADAPTERS = {};

const participantTypeFor = (config) => (config.participantType === 'singles' ? 'individual' : 'team');

// buildBracket's own feeder relationship (see bracketUtils.js): round r
// match index i feeds round r+1 match index floor(i/2), slot A if i even
// else B. Recomputed here from array position rather than trusted off any
// field on the match itself, since the frontend schedule never stored an
// explicit pointer — this exactly mirrors the math the generator used to
// build the bracket in the first place.
const bracketLinkage = (rounds) => {
  const linkage = new Map();
  for (let r = 0; r < rounds.length - 1; r++) {
    const current = rounds[r].matches;
    const next = rounds[r + 1].matches;
    current.forEach((match, i) => {
      const target = next[Math.floor(i / 2)];
      if (target) linkage.set(match.id, { nextMatchTempId: target.id, nextMatchSlot: i % 2 === 0 ? 'A' : 'B' });
    });
  }
  return linkage;
};

const toBackendSlot = (entry, config) => {
  if (!entry || entry.isPlaceholder) return null;
  return { id: entry.id, type: participantTypeFor(config), name: entry.name };
};

// A placeholder qualifier slot fed by standings carries no participant yet,
// only where to look it up once standings are final. Two shapes exist:
// - groupKnockoutGenerator.js's `qualifiers`: id "${groupLabel}-${rank}" (a
//   group label is always "Group X"/a region name — see groupLabels.js —
//   never containing '-', so this split is unambiguous) -> 'group_rank'.
// - pagePlayoffGenerator.js's `standingPlaceholder`: id "standing-${rank}"
//   (no group at all, the league is ungrouped) -> 'overall_rank'.
// Recovered here into the generic seedSource shape the backend's
// resolvePendingSeeds understands, so IT can fill this slot in once results
// are final, instead of this module needing its own per-format seeding logic
// (see this file's module doc comment).
const seedSourceForPlaceholder = (entry) => {
  if (!entry?.isPlaceholder) return null;
  const standingMatch = /^standing-(\d+)$/.exec(entry.id);
  if (standingMatch) return { type: 'overall_rank', rank: parseInt(standingMatch[1], 10) };
  const sep = entry.id.lastIndexOf('-');
  if (sep === -1) return null;
  const groupName = entry.id.slice(0, sep);
  const rank = parseInt(entry.id.slice(sep + 1), 10);
  if (!groupName || !Number.isFinite(rank)) return null;
  return { type: 'group_rank', groupName, rank };
};

// Shared by every section of a schedule that's a plain `[{id, round, label,
// matches}]` array (single_elimination and round_robin ARE this directly;
// group_knockout's group stage and knockout stage are each one of these
// nested inside a bigger envelope — compass_draw/regional_bracket/
// page_playoff/cross_group/ladder aren't wired up to this yet). `withLinkage`
// computes buildBracket-style next-match routing; plain round_robin sections
// have no such routing, every match is independent. `listKey` is which
// MatchListView's override state (see useScheduleBuilder.js) this section's
// gear-icon settings were configured under — 'main' for the two single-
// section formats, `group:${label}` / 'full' for group_knockout (see
// GroupStandingsPanel.jsx / ScheduleResultView.jsx for those exact keys).
// `groupName` tags every match with its group (null for a non-grouped
// format) — needed for backend standings (updateStanding/
// populateKnockoutFromGroups key off tournament_rounds.group_name) and for
// resolveSeedSource to know which group's standings to check.
const serializeFlatRounds = (rounds, ctx, { backendRoundType, withLinkage, listKey = 'main', groupName = null }) => {
  const { courts, roundOverrides, rowOverrides, matchOrderOverrides, tieFormat, tieOverrides } = ctx;
  // Applying tie format here (not just at render time) is what makes an MLP
  // tie's 5-game slate (subMatches — see tieFormats.js) actually reach the
  // rows below as 5 distinct entries instead of never existing at all for
  // save purposes. Every discipline then gets saved as its OWN real
  // tournament_rounds row (see the withLinkage:false branch below) —
  // required for standings/statistics to reflect each game actually played,
  // not just a UI preview of one. A raw (non-eligible, or SINGLE_MATCH) tie
  // is a no-op here, unchanged from before this existed.
  const tieFormattedRounds = applyTieFormatToRounds(rounds, tieFormat, tieOverrides);
  const linkage = withLinkage ? bracketLinkage(tieFormattedRounds) : new Map();
  // Resolves each round's rows to their effective court (row > round >
  // auto-assigned) and saved manual order, same as what MatchListView
  // itself renders — filters/currentUserId are irrelevant to a save, so
  // both are null (nothing should be excluded from what gets persisted).
  const resolvedRounds = buildRoundsWithRows(
    tieFormattedRounds,
    matchOrderOverrides?.[listKey],
    courts,
    roundOverrides?.[listKey],
    rowOverrides?.[listKey],
    null,
    null
  );

  return resolvedRounds.map(({ round, rows }) => ({
    roundNumber: round.round,
    roundName: round.label,
    backendRoundType,
    matches: withLinkage
      ? collapseRowsToOnePerMatch(rows, round, linkage, groupName, ctx.config)
      : rows.map((row) => buildMatchFromRow(row, groupName, ctx.config)),
  }));
};

// A row's own already-resolved fields (slotA/slotB/court/gameType/
// gameTypeLabel/playersA/playersB — all present whether this row is a plain
// match, an MLP discipline sub-row, or a real already-saved discipline row
// loaded back from the backend — see scheduleRows.js's flattenRoundRows) are
// everything needed to save it as its own row. No parent-match lookup
// needed — this is exactly why every discipline of an MLP tie now becomes
// its own real, independent tournament_rounds row instead of collapsing to
// one: each row here already IS a complete, self-contained match.
const buildMatchFromRow = (row, groupName, config) => {
  const seedSourceA = seedSourceForPlaceholder(row.slotA);
  const seedSourceB = seedSourceForPlaceholder(row.slotB);
  const overriddenMatchSettings = {
    ...(row.useCustomSettings ? row.settings : null),
    ...(seedSourceA ? { seedSourceA } : null),
    ...(seedSourceB ? { seedSourceB } : null),
  };
  return {
    tempId: row.key,
    teamA: toBackendSlot(row.slotA, config),
    teamB: toBackendSlot(row.slotB, config),
    court: row.court || null,
    groupName,
    gameType: row.gameType || null,
    gameTypeLabel: row.gameTypeLabel || null,
    overriddenMatchSettings: Object.keys(overriddenMatchSettings).length ? overriddenMatchSettings : null,
    participantAPlayers: row.playersA || null,
    participantBPlayers: row.playersB || null,
    nextMatchTempId: null,
    nextMatchSlot: null,
  };
};

// Bracket sections (withLinkage: true) keep the pre-existing behavior: an
// MLP tie's subMatches still collapse back to one saved row (the first
// discipline's data only), because a bracket's next_round_id/
// loser_next_round_id linkage is inherently per TIE, not per discipline —
// there's no existing rule for "which of 5 independent discipline results
// decides who advances" (that needs its own 3-of-5 rollup design, not
// bundled into this fix). `key` is a composite `${matchId}-${gameType}` for
// subMatches rows, `matchId` alone otherwise; `id` is always a 36-char UUID
// (see generators/idGen.js), so slicing to that fixed length recovers it
// from the composite form without guessing at a separator (a UUID's own
// hyphens make splitting on '-' unsafe).
const collapseRowsToOnePerMatch = (rows, round, linkage, groupName, config) =>
  rows.reduce((acc, row) => {
    const matchId = round.matches.some((m) => m.id === row.key) ? row.key : row.key.slice(0, 36);
    if (acc.seen.has(matchId)) return acc;
    acc.seen.add(matchId);
    const match = round.matches.find((m) => m.id === matchId);
    if (!match) return acc;
    const link = linkage.get(match.id);
    const seedSourceA = seedSourceForPlaceholder(match.slotA);
    const seedSourceB = seedSourceForPlaceholder(match.slotB);
    const overriddenMatchSettings = {
      ...(row.useCustomSettings ? row.settings : null),
      ...(seedSourceA ? { seedSourceA } : null),
      ...(seedSourceB ? { seedSourceB } : null),
    };
    acc.matches.push({
      tempId: match.id,
      teamA: toBackendSlot(match.slotA, config),
      teamB: toBackendSlot(match.slotB, config),
      court: row.court || null,
      groupName,
      overriddenMatchSettings: Object.keys(overriddenMatchSettings).length ? overriddenMatchSettings : null,
      participantAPlayers: row.playersA || null,
      participantBPlayers: row.playersB || null,
      nextMatchTempId: link?.nextMatchTempId || null,
      nextMatchSlot: link?.nextMatchSlot || null,
    });
    return acc;
  }, { seen: new Set(), matches: [] }).matches;

// Inverse of seedSourceForPlaceholder — rebuilds the exact qualifier
// placeholder shape the generator originally produced ("Group A · 1st
// place", isPlaceholder: true) from a still-unresolved slot's saved
// seedSource metadata, so a reloaded bracket shell reads the same as a
// freshly-generated one instead of falling back to a generic "TBD".
const placeholderFromSeedSource = (source) => {
  if (!source) return null;
  if (source.type === 'overall_rank') return { id: `standing-${source.rank}`, name: `${ordinal(source.rank)} place`, isPlaceholder: true };
  if (source.type === 'group_rank') return { id: `${source.groupName}-${source.rank}`, name: `${source.groupName} · ${ordinal(source.rank)} place`, isPlaceholder: true };
  return null;
};

// Mirrors realParticipantsService.js's own `fetchRoster` exactly (same
// endpoint, same active/slice/shape logic) — duplicated rather than shared
// since that file is provider-scoped (only ScheduleBuilderTab.jsx wires it
// in) and this one is the separate save/load path, but a saved team slot's
// roster needs to come back in the identical shape a freshly-generated
// one had, since isMLPEligible (generators/tieFormats.js) and everything
// downstream of it (TeamPairingConfig, MatchSettingsModal) only understand
// that shape.
const fetchTeamRoster = async (teamId, playersPerTeam) => {
  const team = await teamAPI.getById(teamId);
  const members = (team?.members || []).filter((m) => m.isActive).slice(0, playersPerTeam || 4);
  return members.map((m) => ({ id: m.user.id, name: `${m.user.firstName} ${m.user.lastName}`.trim(), gender: null }));
};

// `rosterById` is only ever populated for team slots (see loadSavedSchedule)
// — a hydrated INDIVIDUAL slot never had a roster to begin with, same as a
// freshly-generated one, so leaving it off there isn't a gap, just correct.
// Without this, a reloaded team slot came back as bare `{id, name}` — which
// silently made isMLPEligible false for every backend-hydrated schedule
// (MLP settings survived the round trip, but the "MLP Settings" button and
// its tie-slate display quietly disappeared on reload, since eligibility is
// re-derived from the live roster on every render, not stored as a flag).
const toSlot = (id, type, nameById, rosterById) => {
  if (id == null) return null;
  const roster = type === 'team' ? rosterById?.get(String(id)) : null;
  return {
    id,
    name: nameById.get(String(id)) || nameById.get(id) || (type === 'team' ? 'Team' : 'Player'),
    ...(roster ? { roster } : null),
  };
};

// Untouched, snake_case passthrough of a raw row's own live-tournament
// fields — deliberately NOT reshaped/camelCased so it can go straight into
// tournamentScheduleAPI.startGame/checkIn and matchHandlers.
// buildTeamsDataFromGame (src/tournaments/utils/matchHandlers.js) with no
// adapting, same as those already expect from the old ScheduleTab.jsx. Only
// ever built from a real saved row (both this function's callers only ever
// run against rows that came back from the backend) — its absence on a
// freshly-generated, not-yet-saved schedule's matches is exactly the signal
// MatchDetailModal.jsx uses to know there's nothing live to show yet.
const buildLiveData = (row) => ({
  // The real tournament_rounds id — deliberately NOT the same as a row's
  // own `key` in the frontend row shape (scheduleRows.js's flattenRoundRows):
  // an MLP tie's `key` there is a composite `${matchId}-${gameType}` per
  // discipline row (since the backend still saves the whole tie as ONE row
  // — see this file's module doc comment), so callers that need the real
  // backend id to start/check in a game must use `liveData.id`, never
  // `row.key`.
  id: String(row.id),
  checkin_status: row.checkin_status,
  status: row.status,
  event_id: row.event_id,
  event: row.event,
  sport_type_id: row.tournament?.sport_type_id,
  checkins: row.checkins || [],
  participant_a_type: row.participant_a_type,
  participant_a_id: row.participant_a_id,
  participant_a_name: row.participant_a_name,
  participant_b_type: row.participant_b_type,
  participant_b_id: row.participant_b_id,
  participant_b_name: row.participant_b_name,
  // For completed matches — same fields ScheduleTab.jsx already renders
  // directly (:1759-1760, 1892-1893). participant_a/b_score is the actual
  // final score for a single-set match; score_summary is a comma-joined
  // per-set string ("21-15, 18-25, 15-10") for multi-set sports, built
  // server-side (tournamentScheduleController.js's enrichGameWithCheckins)
  // from game_common's score data — see MatchScoreCell.jsx for how it's parsed.
  participant_a_score: row.participant_a_score,
  participant_b_score: row.participant_b_score,
  score_summary: row.score_summary,
  // Backend-persisted per-match settings override (see applyRowOverride /
  // applyRoundOverride in useScheduleBuilder.js, both of which write here) —
  // surfaced so scheduleRows.js's resolveOverrides can fall back to this on
  // a fresh load, when this session's own rowOverrides/roundOverrides state
  // is empty. Without this, the Match/Round Settings modals always reopened
  // looking un-customized after a refresh even though the backend column
  // driving the actual game creation was correctly set the whole time.
  overridden_match_settings: row.overridden_match_settings || null,
});

// Reconstructs a flat rounds/matches shape directly from real backend rows —
// real ids stand in for the GUIDs a fresh generate() would assign,
// participant names come from `nameById` (this tournament's full roster,
// fetched separately since /rounds doesn't resolve display names itself).
// `withPlaceholders` renders an undecided slot as "TBD" (bracket formats,
// where a later round starts genuinely empty) — plain round_robin has no
// such slots (every match's both sides are known from the start), so it's
// off there; `firstRoundCanBye` marks a one-sided round-1 match as a bye,
// meaningless for round_robin. `roundLabelPrefix`/`startingRoundIndex` let a
// caller (group_knockout's group-stage hydrator) fold this section into a
// bigger round-numbering scheme rather than always restarting at round 1.
const hydrateFlatRounds = (flatRounds, ctx, { withPlaceholders, firstRoundCanBye }) => {
  const { nameById, rosterById } = ctx;
  const byRoundNumber = new Map();
  flatRounds.forEach((row) => {
    if (!byRoundNumber.has(row.round_number)) byRoundNumber.set(row.round_number, []);
    byRoundNumber.get(row.round_number).push(row);
  });
  const roundNumbers = Array.from(byRoundNumber.keys()).sort((a, b) => a - b);

  const rounds = roundNumbers.map((roundNumber, idx) => {
    const rows = byRoundNumber.get(roundNumber).sort((a, b) => a.match_number - b.match_number);
    const matches = rows.map((row) => {
      const settings = row.overridden_match_settings || {};
      const slotA = toSlot(row.participant_a_id, row.participant_a_type, nameById, rosterById)
        || (withPlaceholders ? placeholderFromSeedSource(settings.seedSourceA) : null);
      const slotB = toSlot(row.participant_b_id, row.participant_b_type, nameById, rosterById)
        || (withPlaceholders ? placeholderFromSeedSource(settings.seedSourceB) : null);
      return {
        id: String(row.id),
        number: row.match_number,
        round: idx + 1,
        slotA,
        slotB,
        placeholderA: withPlaceholders && !slotA ? 'TBD' : null,
        placeholderB: withPlaceholders && !slotB ? 'TBD' : null,
        // Set only for a real, already-saved MLP discipline row (see
        // scheduleApi.js's serializeFlatRounds) — `gameType` doubles as the
        // signal tieFormats.js's applyTieFormatToRounds uses to never
        // re-expand this match into another 5-game subMatches slate on top
        // of the 5 real rows already reconstructed here (one per row
        // sharing this round_number — see byRoundNumber grouping above).
        // `playersA`/`playersB` are this specific discipline's 2-of-4
        // roster players, not the whole team — scheduleRows.js's
        // flattenRoundRows reads these directly to render the same name
        // sub-line a pre-save preview subMatches row already showed.
        gameType: row.game_type || null,
        gameTypeLabel: row.game_type_label || null,
        playersA: row.participant_a_players || null,
        playersB: row.participant_b_players || null,
        liveData: buildLiveData(row),
        // A genuinely one-sided match (BYE, advances automatically) can only
        // happen in round 1, from an odd draw — an empty slot in any later
        // round just means "not decided yet" (its feeder match hasn't
        // finished), never a bye.
        isBye: firstRoundCanBye && idx === 0 && Boolean(row.participant_a_id) !== Boolean(row.participant_b_id),
        court: row.court || null,
      };
    });
    return {
      id: `round-${roundNumber}`,
      round: idx + 1,
      // The REAL backend round_number — `round` above is a display-only
      // sequential relabeling after sorting, which stops matching the
      // actual column the moment any round has ever been deleted (a gap in
      // round_number no longer lines up with idx+1). Delete Round/Add Match
      // (useScheduleBuilder.js) need the real value to send to the backend,
      // never the display one.
      realRoundNumber: roundNumber,
      label: rows[0]?.round_name || `Round ${idx + 1}`,
      matches,
    };
  });

  return { rounds, totalRounds: rounds.length };
};

ADAPTERS.single_elimination = {
  serialize: (schedule, ctx) => ({ rounds: serializeFlatRounds(schedule.rounds, ctx, { backendRoundType: 'knockout', withLinkage: true }) }),
  hydrate: (flatRounds, ctx) => {
    const { rounds, totalRounds } = hydrateFlatRounds(flatRounds, ctx, { withPlaceholders: true, firstRoundCanBye: true });
    const participantsCount = rounds[0]?.matches?.length ? rounds[0].matches.length * 2 : 0;
    return { formatId: 'single_elimination', participantsCount, bracketSize: participantsCount, byes: 0, totalRounds, rounds };
  },
};

ADAPTERS.round_robin = {
  serialize: (schedule, ctx) => ({ rounds: serializeFlatRounds(schedule.rounds, ctx, { backendRoundType: 'round_robin', withLinkage: false }) }),
  hydrate: (flatRounds, ctx) => {
    const { rounds } = hydrateFlatRounds(flatRounds, ctx, { withPlaceholders: false, firstRoundCanBye: false });
    const totalMatches = rounds.reduce((sum, r) => sum + r.matches.length, 0);
    const participantsCount = rounds[0]?.matches?.length ? rounds[0].matches.length * 2 : 0;
    return { formatId: 'round_robin', participantsCount, totalMatches, rounds };
  },
};

// group_knockout: group stage is N independent round_robin sections (one per
// group, each its own listKey `group:${label}` — see GroupStandingsPanel.jsx
// — and its own group_name tag); knockout stage is one single_elimination-
// shaped bracket (listKey 'full', the only view a knockout match's gear icon
// is ever reachable from — BracketBoard itself has no override controls),
// whose round-1 slots are qualifier placeholders resolved via seedSource
// instead of real participants (see seedSourceForPlaceholder above) rather
// than the fixed bracket-position linkage every other round uses.
ADAPTERS.group_knockout = {
  serialize: (schedule, ctx) => {
    const groupRounds = schedule.groupStage.flatMap((group) =>
      serializeFlatRounds(group.rounds, ctx, {
        backendRoundType: 'round_robin',
        withLinkage: false,
        listKey: `group:${group.label}`,
        groupName: group.label,
      })
    );
    const maxGroupRound = groupRounds.reduce((max, r) => Math.max(max, r.roundNumber), 0);
    const knockoutRounds = serializeFlatRounds(schedule.knockout, ctx, {
      backendRoundType: 'knockout',
      withLinkage: true,
      listKey: 'full',
    }).map((r) => ({ ...r, roundNumber: maxGroupRound + r.roundNumber }));

    return { rounds: [...groupRounds, ...knockoutRounds] };
  },
  hydrate: (flatRounds, ctx) => {
    const { nameById, rosterById, config } = ctx;
    const groupRows = flatRounds.filter((r) => r.group_name);
    const knockoutRows = flatRounds.filter((r) => !r.group_name);

    const groupLabels = Array.from(new Set(groupRows.map((r) => r.group_name))).sort();
    const groupStage = groupLabels.map((label) => {
      const rowsForGroup = groupRows.filter((r) => r.group_name === label);
      const { rounds } = hydrateFlatRounds(rowsForGroup, ctx, { withPlaceholders: false, firstRoundCanBye: false });
      rounds.forEach((round) => round.matches.forEach((m) => { m.groupLabel = label; }));
      const participantIds = new Map();
      rowsForGroup.forEach((r) => {
        if (r.participant_a_id) participantIds.set(r.participant_a_id, toSlot(r.participant_a_id, r.participant_a_type, nameById, rosterById));
        if (r.participant_b_id) participantIds.set(r.participant_b_id, toSlot(r.participant_b_id, r.participant_b_type, nameById, rosterById));
      });
      return { label, participants: Array.from(participantIds.values()), rounds, totalMatches: rowsForGroup.length };
    });

    const { rounds: knockout } = hydrateFlatRounds(knockoutRows, ctx, { withPlaceholders: true, firstRoundCanBye: false });

    // Mirrors groupKnockoutGenerator.js's own buildExecutionOrder — every
    // group's round N happens "simultaneously" under one merged round
    // label, followed by the knockout stage — so the merged "Full Schedule"
    // list view reads the same after a reload as it did fresh.
    const maxGroupRounds = Math.max(0, ...groupStage.map((g) => g.rounds.length));
    const execGroupRounds = [];
    for (let r = 0; r < maxGroupRounds; r++) {
      const matches = groupStage.flatMap((g) => (g.rounds[r]?.matches || []).map((m) => ({ ...m, groupLabel: g.label })));
      execGroupRounds.push({ id: `exec-group-${r + 1}`, round: r + 1, label: `Group Stage — Round ${r + 1}`, matches });
    }
    const execKnockout = knockout.map((r) => ({ id: `exec-knockout-${r.round}`, round: maxGroupRounds + r.round, label: `Knockout — ${r.label}`, matches: r.matches }));
    const executionOrder = [...execGroupRounds, ...execKnockout];

    const knockoutBracketSize = knockout[0]?.matches?.length ? knockout[0].matches.length * 2 : 0;
    return {
      formatId: 'group_knockout',
      participantsCount: config.participantCount || groupStage.reduce((sum, g) => sum + g.participants.length, 0),
      groupCount: groupStage.length,
      advancePerGroup: config.groupingConfig?.advancePerGroup || 2,
      groupStage,
      knockout,
      knockoutBracketSize,
      executionOrder,
    };
  },
};

// cross_group: every match is an independent cross-group tie — no bracket
// linkage, no group-scoped standings/seeding (nothing ever advances INTO a
// cross_group tie from group results the way group_knockout's knockout
// stage does), so this is a flat rounds/matches list like round_robin, just
// under listKey 'matches' (see CrossGroupView.jsx) instead of 'main', and
// with `group_name` left null on every match (a tie's `groupLabel` describes
// which two GROUPS met, e.g. "Group A vs Group B" — not a single-group
// standings scope, so it doesn't belong in the group_name column the way
// group_knockout's per-group matches do).
ADAPTERS.cross_group = {
  serialize: (schedule, ctx) => ({
    rounds: serializeFlatRounds(schedule.rounds, ctx, { backendRoundType: 'round_robin', withLinkage: false, listKey: 'matches' }),
  }),
  hydrate: (flatRounds, ctx) => {
    const { rounds } = hydrateFlatRounds(flatRounds, ctx, { withPlaceholders: false, firstRoundCanBye: false });
    // Each cross_group round is exactly one tie, labeled by its two
    // participants — reconstructed the same way the generator itself
    // titles it, from that round's own (single, in the non-legs case) match.
    rounds.forEach((round) => {
      const first = round.matches[0];
      round.label = first ? `${first.slotA?.name || 'TBD'} vs ${first.slotB?.name || 'TBD'}` : round.label;
    });
    const totalMatches = rounds.reduce((sum, r) => sum + r.matches.length, 0);
    const participantsCount = new Set(rounds.flatMap((r) => r.matches.flatMap((m) => [m.slotA?.id, m.slotB?.id].filter(Boolean)))).size;
    // `groups`/`groupCount` aren't reconstructed (CrossGroupView.jsx never
    // reads schedule.groups — only schedule.rounds — so there's nothing to
    // rebuild them FOR); left empty rather than guessed at.
    return { formatId: 'cross_group', participantsCount, groupCount: 0, groups: [], rounds, totalMatches };
  },
};

// regional_bracket: each region is its OWN full single_elimination-shaped
// bracket (not round_robin, unlike group_knockout's group stage), and region
// WINNERS feed a Final Stage bracket. Since a region's champion is just
// "whoever wins that region's own final match," this needs no seedSource/
// resolvePendingSeeds involvement at all — it's pure bracket linkage
// (next_round_id), exactly like a normal round-to-round advance, just
// crossing from one bracket SECTION into another. `group_name` is repurposed
// here purely as a hydration discriminator (which region a knockout row
// belongs to) — harmless since knockout rows never go through
// updateStanding/recalculateStandings anyway.
ADAPTERS.regional_bracket = {
  serialize: (schedule, ctx) => {
    const finalStageRounds = serializeFlatRounds(schedule.finalStage, ctx, {
      backendRoundType: 'knockout',
      withLinkage: true,
      listKey: 'final',
    });
    const finalStageFirstRound = finalStageRounds[0];
    // Mirrors buildBracket's own seeding (bracketUtils.js): the finalStage
    // bracket was built from `champions` in region order, seeded via
    // buildSeedOrder — so region i's champion (seed i+1) lands at whichever
    // position that seed occupies in the seed order, not necessarily
    // position i itself.
    const seedOrder = buildSeedOrder(schedule.finalStageBracketSize);

    let maxRegionRound = 0;
    const regionRounds = schedule.regions.flatMap((region, i) => {
      const rounds = serializeFlatRounds(region.rounds, ctx, {
        backendRoundType: 'knockout',
        withLinkage: true,
        listKey: `region:${region.label}`,
        groupName: region.label,
      });
      maxRegionRound = Math.max(maxRegionRound, ...rounds.map((r) => r.roundNumber));

      // The region's own final is always its LAST round's one match (see
      // buildBracket — every round after the first halves the match count
      // down to exactly 1). Link its winner straight into the finalStage
      // slot this region's champion placeholder was seeded into.
      const finalRound = rounds[rounds.length - 1];
      const regionFinalMatch = finalRound?.matches?.[0];
      if (regionFinalMatch && finalStageFirstRound) {
        const orderIdx = seedOrder.indexOf(i + 1);
        const targetMatch = finalStageFirstRound.matches[Math.floor(orderIdx / 2)];
        if (targetMatch) {
          regionFinalMatch.nextMatchTempId = targetMatch.tempId;
          regionFinalMatch.nextMatchSlot = orderIdx % 2 === 0 ? 'A' : 'B';
        }
      }
      return rounds;
    });

    const offsetFinalStageRounds = finalStageRounds.map((r) => ({ ...r, roundNumber: maxRegionRound + r.roundNumber }));
    return { rounds: [...regionRounds, ...offsetFinalStageRounds] };
  },
  hydrate: (flatRounds, ctx) => {
    const { config } = ctx;
    const regionRows = flatRounds.filter((r) => r.group_name);
    const finalStageRows = flatRounds.filter((r) => !r.group_name);

    const regionLabels = Array.from(new Set(regionRows.map((r) => r.group_name)));
    const regions = regionLabels.map((label) => {
      const { rounds } = hydrateFlatRounds(regionRows.filter((r) => r.group_name === label), ctx, {
        withPlaceholders: true,
        firstRoundCanBye: true,
      });
      const participantsCount = rounds[0]?.matches?.length ? rounds[0].matches.length * 2 : 0;
      return { label, participantsCount, bracketSize: participantsCount, byes: 0, totalRounds: rounds.length, rounds };
    });

    const { rounds: finalStage } = hydrateFlatRounds(finalStageRows, ctx, { withPlaceholders: true, firstRoundCanBye: false });
    const finalStageBracketSize = finalStage[0]?.matches?.length ? finalStage[0].matches.length * 2 : 0;

    return {
      formatId: 'regional_bracket',
      participantsCount: config.participantCount || regions.reduce((sum, r) => sum + r.participantsCount, 0),
      regionCount: regions.length,
      regions,
      finalStage,
      finalStageBracketSize,
    };
  },
};

// compass_draw: main (East) bracket + up to 7 direction-named consolation
// brackets, each fed by a specific ROUND of a specific parent bracket's
// LOSERS (see compassDrawGenerator.js's dependency chain). Unlike
// group_knockout's group->knockout handoff, this needs no seedSource/
// resolvePendingSeeds at all — the backend already has a dedicated LOSER
// routing primitive parallel to next_round_id (`loser_next_round_id`/
// `loser_next_round_slot`, consumed automatically by advanceLoser), so this
// is pure cross-bracket linkage, same technique as regional_bracket's
// region->final-stage WINNER linkage just using the loser fields instead.
const COMPASS_LOSER_LINKS = [
  ['E', 0, 'W'],   // Main Draw R1 losers -> West
  ['W', 0, 'N'],   // West R1 losers -> North
  ['N', 0, 'NE'],  // North R1 losers -> Northeast
  ['W', 1, 'NW'],  // West R2 losers -> Northwest
  ['E', 1, 'S'],   // Main Draw R2 losers -> South
  ['S', 0, 'SW'],  // South R1 losers -> Southwest
  ['SW', 0, 'SE'], // Southwest R1 losers -> Southeast
];
const COMPASS_DIRECTION_ORDER = ['E', 'W', 'N', 'NE', 'NW', 'S', 'SW', 'SE'];

ADAPTERS.compass_draw = {
  serialize: (schedule, ctx) => {
    const { groups, activeDirections } = schedule;
    const byDirection = {};
    activeDirections.forEach((dir) => {
      byDirection[dir] = serializeFlatRounds(groups[dir].rounds, ctx, {
        backendRoundType: 'knockout',
        withLinkage: true,
        listKey: `compass:${dir}`,
        groupName: dir,
      });
    });

    COMPASS_LOSER_LINKS.forEach(([sourceDir, sourceRoundIdx, targetDir]) => {
      const sourceRound = byDirection[sourceDir]?.[sourceRoundIdx];
      const targetFirstRound = byDirection[targetDir]?.[0];
      if (!sourceRound || !targetFirstRound) return;
      // Mirrors buildBracket's own seeding, same as regional_bracket's
      // champion linkage: sourceRound's match i (seed i+1) fed
      // losersOf(...)[i] into the target bracket, which then seeded it via
      // buildSeedOrder — not necessarily target round-1 match i itself.
      const seedOrder = buildSeedOrder(targetFirstRound.matches.length * 2);
      sourceRound.matches.forEach((match, i) => {
        const orderIdx = seedOrder.indexOf(i + 1);
        const targetMatch = orderIdx === -1 ? null : targetFirstRound.matches[Math.floor(orderIdx / 2)];
        if (targetMatch) {
          match.loserNextMatchTempId = targetMatch.tempId;
          match.loserNextMatchSlot = orderIdx % 2 === 0 ? 'A' : 'B';
        }
      });
    });

    return { rounds: activeDirections.flatMap((dir) => byDirection[dir]) };
  },
  hydrate: (flatRounds, ctx) => {
    const { config } = ctx;
    const byDirection = {};
    flatRounds.forEach((row) => {
      const dir = row.group_name;
      if (!byDirection[dir]) byDirection[dir] = [];
      byDirection[dir].push(row);
    });

    const activeDirections = COMPASS_DIRECTION_ORDER.filter((d) => byDirection[d]?.length);
    const groups = {};
    activeDirections.forEach((dir) => {
      const { rounds } = hydrateFlatRounds(byDirection[dir], ctx, { withPlaceholders: true, firstRoundCanBye: dir === 'E' });
      const participantsCount = rounds[0]?.matches?.length ? rounds[0].matches.length * 2 : 0;
      groups[dir] = { label: dir, participantsCount, bracketSize: participantsCount, byes: 0, totalRounds: rounds.length, rounds };
    });

    return {
      formatId: 'compass_draw',
      participantsCount: config.participantCount || groups.E?.participantsCount || 0,
      activeDirections,
      groups,
    };
  },
};

// page_playoff (IPL/Double Chance Playoff): a round_robin league + a FIXED
// 4-match ladder (Qualifier 1, Eliminator, Qualifier 2, Final) — never a
// generic bracket, so the generic `bracketLinkage` position math (round r
// index i -> round r+1 index floor(i/2)) is WRONG here (it would send
// Qualifier 1's WINNER into Qualifier 2, when the real rule sends the
// winner to the Final and only the LOSER to Qualifier 2). Routed explicitly
// instead, by each match's own `stage` label, matching the backend's own
// pre-existing generateIPLStyle linkage exactly (Qualifier 1: winner->Final
// slot A, loser->Qualifier 2 slot A; Eliminator: winner->Qualifier 2 slot B;
// Qualifier 2: winner->Final slot B). Round-1 slots (seeds 1-4) use the
// generic 'overall_rank' seedSource — this league has no groups, so it's
// exactly the ungrouped case resolveSeedSource already supports.
const PLAYOFF_STAGE_META = [
  { stage: 'Qualifier 1', note: 'Winner advances straight to the Final. Loser gets one more chance in Qualifier 2.' },
  { stage: 'Eliminator', note: 'Loser is eliminated. Winner advances to Qualifier 2.' },
  { stage: 'Qualifier 2', note: 'Winner advances to the Final.' },
  { stage: 'Final', note: 'Championship match.' },
];

ADAPTERS.page_playoff = {
  serialize: (schedule, ctx) => {
    const leagueRounds = serializeFlatRounds(schedule.leagueRounds, ctx, {
      backendRoundType: 'round_robin',
      withLinkage: false,
      listKey: 'league',
    });
    const playoffRounds = serializeFlatRounds(schedule.playoff, ctx, {
      backendRoundType: 'knockout',
      withLinkage: false,
      // PagePlayoffView.jsx has no gear-icon override controls of its own
      // (unlike a MatchListView-rendered section) — the only place a
      // playoff match's court/settings could have been configured is the
      // merged "Full Schedule" list view, listKey 'full'.
      listKey: 'full',
    });

    const stageToTempId = {};
    schedule.playoff.forEach((round) => round.matches.forEach((m) => { stageToTempId[m.stage] = m.id; }));
    const allPlayoffMatches = playoffRounds.flatMap((r) => r.matches);
    const byStage = (stage) => allPlayoffMatches.find((m) => m.tempId === stageToTempId[stage]);
    const q1 = byStage('Qualifier 1');
    const eliminator = byStage('Eliminator');
    const q2 = byStage('Qualifier 2');
    const final = byStage('Final');
    if (q1 && final) Object.assign(q1, { nextMatchTempId: final.tempId, nextMatchSlot: 'A' });
    if (q1 && q2) Object.assign(q1, { loserNextMatchTempId: q2.tempId, loserNextMatchSlot: 'A' });
    if (eliminator && q2) Object.assign(eliminator, { nextMatchTempId: q2.tempId, nextMatchSlot: 'B' });
    if (q2 && final) Object.assign(q2, { nextMatchTempId: final.tempId, nextMatchSlot: 'B' });

    const offsetPlayoffRounds = playoffRounds.map((r) => ({ ...r, roundNumber: schedule.leagueRounds.length + r.roundNumber }));
    return { rounds: [...leagueRounds, ...offsetPlayoffRounds] };
  },
  hydrate: (flatRounds, ctx) => {
    const { nameById, rosterById, config } = ctx;
    const leagueRows = flatRounds.filter((r) => r.round_type === 'round_robin');
    const playoffRows = flatRounds
      .filter((r) => r.round_type === 'knockout')
      .sort((a, b) => a.round_number - b.round_number || a.match_number - b.match_number);

    const { rounds: leagueRounds } = hydrateFlatRounds(leagueRows, ctx, { withPlaceholders: false, firstRoundCanBye: false });

    const toPlayoffMatch = (row, meta, fallbackPlaceholderA, fallbackPlaceholderB) => {
      const settings = row?.overridden_match_settings || {};
      const slotA = row && (toSlot(row.participant_a_id, row.participant_a_type, nameById, rosterById) || placeholderFromSeedSource(settings.seedSourceA));
      const slotB = row && (toSlot(row.participant_b_id, row.participant_b_type, nameById, rosterById) || placeholderFromSeedSource(settings.seedSourceB));
      return {
        id: row ? String(row.id) : meta.stage,
        number: row?.match_number,
        stage: meta.stage,
        note: meta.note,
        slotA: slotA || null,
        slotB: slotB || null,
        placeholderA: slotA ? null : fallbackPlaceholderA || null,
        placeholderB: slotB ? null : fallbackPlaceholderB || null,
        court: row?.court || null,
        liveData: row ? buildLiveData(row) : null,
      };
    };

    const [q1Row, elimRow, q2Row, finalRow] = playoffRows;
    const playoff = [
      {
        id: 'playoff-r1',
        round: 1,
        label: 'Qualifier 1 & Eliminator',
        matches: [toPlayoffMatch(q1Row, PLAYOFF_STAGE_META[0]), toPlayoffMatch(elimRow, PLAYOFF_STAGE_META[1])],
      },
      {
        id: 'playoff-r2',
        round: 2,
        label: 'Qualifier 2',
        matches: [toPlayoffMatch(q2Row, PLAYOFF_STAGE_META[2], 'Loser of Qualifier 1', 'Winner of Eliminator')],
      },
      {
        id: 'playoff-r3',
        round: 3,
        label: 'Final',
        matches: [toPlayoffMatch(finalRow, PLAYOFF_STAGE_META[3], 'Winner of Qualifier 1', 'Winner of Qualifier 2')],
      },
    ];

    const executionOrder = [
      ...leagueRounds.map((r) => ({ ...r, label: `League — ${r.label}` })),
      ...playoff.map((r) => ({ ...r, round: leagueRounds.length + r.round, label: `Playoffs — ${r.label}` })),
    ];

    return {
      formatId: 'page_playoff',
      participantsCount: config.participantCount || (leagueRounds[0]?.matches?.length ? leagueRounds[0].matches.length * 2 : 0),
      legs: config.legs || 1,
      leagueRounds,
      playoff,
      executionOrder,
    };
  },
};

export const isSaveSupported = (formatId) => !!ADAPTERS[formatId];

/**
 * Persists the current schedule + every manual override to the real
 * backend, reusing the existing `format: 'custom'` blind-save endpoint.
 * `courts`/`roundOverrides`/`rowOverrides`/`matchOrderOverrides` are this
 * module's raw overlay state (see useScheduleBuilder.js) — their resolved
 * effect gets baked into the saved rows by the format's own serializer,
 * not persisted as-is (see this file's module doc comment for why).
 */
export const saveSchedule = async ({
  tournamentId,
  formatId,
  schedule,
  config,
  viewMode,
  tieFormat,
  tieOverrides,
  filters,
  roundOverrides,
  rowOverrides,
  matchOrderOverrides,
}) => {
  const adapter = ADAPTERS[formatId];
  if (!adapter) throw new Error(`Save Schedule isn't wired up for the "${formatId}" format yet`);

  const { rounds } = adapter.serialize(schedule, { config, courts: config.courts, roundOverrides, rowOverrides, matchOrderOverrides, tieFormat, tieOverrides });

  return tournamentAPI.generateSchedule(tournamentId, {
    format: 'custom',
    config: {
      scheduleData: { rounds },
      moduleFormat: formatId,
      moduleConfig: {
        sportType: config.sportType,
        sportPlayedBy: config.sportPlayedBy,
        participantType: config.participantType,
        participantCount: config.participantCount,
        playersPerTeam: config.playersPerTeam,
        legs: config.legs,
        groupingConfig: config.groupingConfig,
        ladderRounds: config.ladderRounds,
        courts: config.courts,
      },
      viewState: { viewMode, tieFormat, tieOverrides, filters },
    },
  });
};

/**
 * Loads a previously-saved schedule back into this module's in-memory
 * shape, or returns null when this tournament has nothing saved yet (a
 * fresh generate-schedule call, `format` other than the ones this module
 * itself saves, or none at all).
 */
export const loadSavedSchedule = async (tournamentId) => {
  const [tournament, flatRounds] = await Promise.all([
    tournamentAPI.getById(tournamentId),
    tournamentScheduleAPI.getFilteredRounds(tournamentId),
  ]);

  const scheduleConfig = tournament?.scheduleConfig;
  const formatId = scheduleConfig?.moduleFormat;
  const adapter = formatId && ADAPTERS[formatId];
  if (!adapter || !flatRounds?.length) return null;

  const nameById = new Map();
  const teamIds = new Set();
  flatRounds.forEach((row) => {
    if (row.participant_a_id && row.participant_a_name) nameById.set(String(row.participant_a_id), row.participant_a_name);
    if (row.participant_b_id && row.participant_b_name) nameById.set(String(row.participant_b_id), row.participant_b_name);
    if (row.participant_a_type === 'team' && row.participant_a_id) teamIds.add(row.participant_a_id);
    if (row.participant_b_type === 'team' && row.participant_b_id) teamIds.add(row.participant_b_id);
  });

  const moduleConfig = scheduleConfig.moduleConfig || {};
  const viewState = scheduleConfig.viewState || {};

  // Every team slot's roster, refetched here since the flat round rows
  // themselves only ever carry an id + display name (see toSlot's doc
  // comment) — without this, a reloaded schedule's team slots come back
  // roster-less and isMLPEligible (tieFormats.js) can never be true again,
  // silently dropping MLP tie-slate support on every backend-loaded
  // schedule even though the MLP settings themselves (tieFormat/tieOverrides,
  // both in viewState above) survive the round trip untouched. No bulk
  // "every team + roster" endpoint exists — one GET /teams/:id per distinct
  // team id in this schedule, in parallel, same as realParticipantsService.js's
  // own fetchRoster does at generation time. A single team's fetch failing
  // just leaves that one slot without a roster (falls back to however it
  // was before this fix — never MLP-eligible) rather than failing the load.
  const rosterById = new Map();
  await Promise.all(
    Array.from(teamIds).map(async (teamId) => {
      try {
        rosterById.set(String(teamId), await fetchTeamRoster(teamId, moduleConfig.playersPerTeam));
      } catch {
        // Best-effort — see comment above.
      }
    })
  );

  const schedule = adapter.hydrate(flatRounds, { nameById, rosterById, config: moduleConfig });

  return { formatId, config: moduleConfig, schedule, viewState };
};
